import type { ProjectActivityModel } from '@xver/apis/v1/projects/model';
import { Button } from '@xver/components/Atoms';
import DialogBox from '@xver/components/Atoms/DialogBox/DialogBox';
import { useProjectActivityQuery } from '@xver/hooks/useProjectQueries';
import { ActivityDetail, activityActorLabel, activityDetail } from '@xver/utils/projects';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccessSummary, DateTime } from '../../ProjectsPage/components/ProjectBits';
import { LoadError } from './MembersTabPanel';

function ActivityDetailText({ detail }: { detail: ActivityDetail }) {
    const { t } = useTranslation('projects');
    switch (detail.kind) {
        case 'move': {
            const values = {
                type: t(`move.types.${detail.resourceType}`),
                project: detail.projectName,
            };
            return (
                <>
                    {detail.direction === 'in'
                        ? t('activity.detail.movedIn', values)
                        : t('activity.detail.movedOut', values)}
                </>
            );
        }
        case 'access':
            return (
                <>
                    {t('requests.invitation.role', { role: t(`roles.${detail.role}`) })}
                    {' · '}
                    <AccessSummary role={detail.role} access={detail.access} />
                </>
            );
        case 'privateIp':
            return <>{t('activity.detail.privateIp', { ip: detail.privateIp })}</>;
        default:
            return <>{detail.text}</>;
    }
}

function ActivityDetailLine({ item }: { item: ProjectActivityModel }) {
    const detail = activityDetail(item);
    if (!detail) return null;
    return (
        <p className="text-sm text-fg/78 break-words">
            <ActivityDetailText detail={detail} />
        </p>
    );
}

export default function ActivityTabPanel({ projectId }: { projectId: string }) {
    const { t } = useTranslation('projects');
    const { data, isPending, isError, refetch, hasNextPage, fetchNextPage, isFetchingNextPage } =
        useProjectActivityQuery(projectId);
    const listRef = useRef<HTMLOListElement>(null);
    const [status, setStatus] = useState('');
    const pendingFocusIndexRef = useRef<number | null>(null);
    const items = data?.pages.flatMap((page) => page.data ?? []) ?? [];

    useEffect(() => {
        const index = pendingFocusIndexRef.current;
        const item = index === null ? undefined : listRef.current?.children[index];
        if (item instanceof HTMLElement) {
            pendingFocusIndexRef.current = null;
            item.focus();
        }
    }, [items.length]);

    const loadMore = async () => {
        const before = items.length;
        const result = await fetchNextPage();
        const after = result.data?.pages.flatMap((page) => page.data ?? []).length ?? before;
        setStatus(t('activity.loaded', { count: after - before }));
        if (!result.hasNextPage) {
            const next = listRef.current?.children[before];
            if (next instanceof HTMLElement) next.focus();
            else pendingFocusIndexRef.current = before;
        }
    };

    return (
        <DialogBox label={t('activity.title')} description={t('activity.description')}>
            {isPending ? (
                <p role="status" aria-busy="true" className="w-full p-4 text-sm text-fg/78">
                    {t('activity.loading')}
                </p>
            ) : isError ? (
                <LoadError onRetry={() => refetch()} />
            ) : items.length === 0 ? (
                <p className="w-full p-6 text-sm text-fg/68 text-center">{t('activity.empty')}</p>
            ) : (
                <>
                    <ol ref={listRef} className="w-full">
                        {items.map((item) => (
                            <li
                                key={item.id}
                                tabIndex={-1}
                                className="w-full px-4 py-3 flex flex-col gap-0.5 border-t-[0.5px] border-line first:border-t-0"
                            >
                                <p className="text-sm text-fg break-words">
                                    <span className="font-medium">
                                        {activityActorLabel(item, t)}
                                    </span>{' '}
                                    {t(`activity.actions.${item.action}`, {
                                        subject: item.subject || t('activity.someoneSubject'),
                                        defaultValue: item.action,
                                    })}
                                </p>
                                <ActivityDetailLine item={item} />
                                <p className="text-xs text-fg/68">
                                    <DateTime iso={item.createdAt} />
                                </p>
                            </li>
                        ))}
                    </ol>
                    {hasNextPage && (
                        <div className="w-full flex justify-center px-4 py-3 border-t-[0.5px] border-line">
                            <Button
                                size="lg"
                                variant="secondary"
                                aria-busy={isFetchingNextPage || undefined}
                                onClick={isFetchingNextPage ? undefined : loadMore}
                            >
                                {isFetchingNextPage
                                    ? t('activity.loading')
                                    : t('activity.loadMore')}
                            </Button>
                        </div>
                    )}
                </>
            )}
            <p role="status" className="sr-only">
                {status}
            </p>
        </DialogBox>
    );
}
